import { env } from '@/lib/env'
import { trackEvent, trackException } from '@/lib/telemetry'
import { prepareInlineEmailImage } from '@/lib/proposal-email-template'
import { QuoteType } from '@/lib/types'
import { useQuery } from '@tanstack/react-query'
import { z } from 'zod'
import { dynamicsApi } from './client'
import { createRecord, getMultipleRecords, getRecord, updateRecord } from './helpers'
import { QUERY_KEYS } from './query-keys'
import type { BranchClientLogo } from '@/lib/branch-client-logo'

// Email signature type and query
const whoAmIResponseSchema = z.object({
  UserId: z.string(),
  BusinessUnitId: z.string(),
  OrganizationId: z.string(),
})

const currentUserSchema = z.object({
  fullname: z.string().nullable(),
})

export async function getCurrentUserId(): Promise<string> {
  const res = await dynamicsApi.get('/WhoAmI')
  return whoAmIResponseSchema.parse(res.data).UserId
}

type RetrieveEmailSignatureResponse = {
  SignatureText: string
}

function decodeHtmlEntities(html: string): string {
  // Decode common HTML entities
  return html
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&amp;/g, '&')
    .replace(/&#13;&#10;/g, '\n')
    .replace(/&#160;/g, ' ')
}

async function retrieveEmailSignature(senderId: string): Promise<string | null> {
  try {
    const response = await dynamicsApi.post<RetrieveEmailSignatureResponse>('/RetrieveEmailSignature', {
      SignatureId: '00000000-0000-0000-0000-000000000000',
      SenderId: `{${senderId.toUpperCase()}}`,
      SenderType: 'systemuser',
    })

    if (response.data.SignatureText) {
      return decodeHtmlEntities(response.data.SignatureText)
    }

    return null
  } catch (error) {
    trackException(error, { context: 'emails.retrieveEmailSignature' })
    return null
  }
}

export function useCurrentUserEmailSignatureQuery() {
  return useQuery({
    queryKey: QUERY_KEYS.currentUserEmailSignature,
    queryFn: async () => {
      const currentUserId = await getCurrentUserId()
      const [signature, currentUser] = await Promise.all([
        retrieveEmailSignature(currentUserId),
        getRecord('systemusers', currentUserId, '?$select=fullname'),
      ])

      return {
        signature,
        senderName: currentUserSchema.parse(currentUser).fullname,
      }
    },
  })
}

// Email sending types and function
type SendEmailParams = {
  quoteId: string
  contactId: string
  contactEmail: string | undefined | null
  toEmails: string[]
  ccEmails: string[]
  fromUserId: string
  subject: string
  body: string
  quoteType: QuoteType | undefined | null
  workOrderId: string | undefined | null
  opportunityId: string | undefined | null
  updateContactEmail: boolean | undefined | null
  inlineImage?: BranchClientLogo
}

type EmailActivityParty = {
  'partyid_contact@odata.bind'?: string
  'partyid_systemuser@odata.bind'?: string
  addressused?: string
  participationtypemask: number // 1 = From, 2 = To, 3 = Cc, 4 = Bcc
}

type MailboxData = {
  mailboxid: string
  enabledforoutgoingemail: boolean
}

async function checkSenderMailboxEnabled(userId: string): Promise<boolean> {
  // Get the mailbox associated with the user
  const mailboxes = await getMultipleRecords<MailboxData>(
    'mailboxes',
    `?$select=mailboxid,enabledforoutgoingemail&$filter=_regardingobjectid_value eq '${userId}'`
  )

  if (mailboxes.length === 0) {
    throw new Error('No mailbox found for the sender. Please configure your mailbox in Dynamics 365.')
  }

  if (!mailboxes[0].enabledforoutgoingemail) {
    throw new Error(
      'Your mailbox is not enabled for outgoing email. Please enable outgoing email in your mailbox settings or contact your administrator.'
    )
  }

  return true
}

function wrapEmailBodyWithFontStyle(htmlBody: string): string {
  const fontStyles = {
    'font-family': 'Segoe UI, sans-serif',
    'font-size': 'medium',
    'font-weight': 'normal',
  }

  // Elements to apply font styles to
  const targetElements = [
    'p',
    'span',
    'div',
    'a',
    'em',
    'li',
    'ul',
    'ol',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'td',
    'th',
    'label',
  ]

  // Build the font style string
  const fontStyleString = Object.entries(fontStyles)
    .map(([prop, value]) => `${prop}: ${value} !important`)
    .join('; ')

  // Process each target element and apply/override font styles
  let processedHtml = htmlBody

  for (const tag of targetElements) {
    // Match opening tags with existing style attribute
    const styleAttrRegex = new RegExp(`<${tag}([^>]*?)\\s+style=["']([^"']*)["']([^>]*)>`, 'gi')
    processedHtml = processedHtml.replace(styleAttrRegex, (_match, before, existingStyle, after) => {
      // Parse existing styles and filter out font-related ones
      const existingStyles = existingStyle
        .split(';')
        .map((s: string) => s.trim())
        .filter((s: string) => {
          if (!s) return false
          const prop = s.split(':')[0]?.trim().toLowerCase()
          // Remove existing font-family, font-size, font-weight
          return !Object.keys(fontStyles).includes(prop)
        })

      // Combine remaining styles with our font styles
      const combinedStyles = existingStyles.concat(fontStyleString).filter(Boolean).join('; ')
      return `<${tag}${before} style="${combinedStyles}"${after}>`
    })

    // Match opening tags without style attribute
    const noStyleRegex = new RegExp(`<${tag}((?![^>]*style=)[^>]*)>`, 'gi')
    processedHtml = processedHtml.replace(noStyleRegex, (_match, attrs) => {
      return `<${tag}${attrs} style="${fontStyleString}">`
    })
  }

  // Wrap in a container div with the font styles as well
  return `<div style="${fontStyleString}">${processedHtml}</div>`
}

export async function sendProposalEmail({
  quoteId,
  contactId,
  contactEmail,
  toEmails,
  ccEmails,
  fromUserId,
  subject,
  body,
  quoteType,
  workOrderId,
  opportunityId,
  updateContactEmail,
  inlineImage,
}: SendEmailParams): Promise<{ success: boolean; emailId: string }> {
  if (toEmails.length === 0) {
    throw new Error('At least one recipient is required.')
  }

  await checkSenderMailboxEnabled(fromUserId)

  const normalizedContactEmail = contactEmail?.trim().toLowerCase() ?? ''
  const isContactEmailMatch = (email: string) => email.trim().toLowerCase() === normalizedContactEmail

  // Update the contact's primary email when a single recipient differs from current contact email
  const singleNewEmail =
    toEmails.length === 1 && updateContactEmail && !isContactEmailMatch(toEmails[0]) ? toEmails[0] : null
  if (singleNewEmail) {
    await updateRecord('contacts', contactId, { emailaddress1: singleNewEmail }, true)
  }

  const promises = []

  // Determine the regarding entity based on quote type and related records
  let regardingBinding: Record<string, string> = {}
  let regardingDescription = ''
  let regardingUrl =
    env.VITE_DYNAMICS_BASE_URL + `/main.aspx?appid=33c294e0-f49c-ef11-8a6b-000d3a56b8ad&pagetype=entityrecord`

  if (quoteType === QuoteType.ServiceInvoice && workOrderId) {
    // Service quote with work order - regarding is the work order
    regardingBinding = { 'regardingobjectid_msdyn_workorder@odata.bind': `/msdyn_workorders(${workOrderId})` }
    regardingDescription = `Work Order (${workOrderId})`
    regardingUrl += `&etn=msdyn_workorder&id=${workOrderId}`
  } else if (opportunityId) {
    // Quote with opportunity - regarding is the opportunity
    regardingBinding = { 'regardingobjectid_opportunity@odata.bind': `/opportunities(${opportunityId})` }
    regardingDescription = `Opportunity (${opportunityId})`
    regardingUrl += `&etn=opportunity&id=${opportunityId}`
  } else {
    // Default - regarding is the quote
    regardingBinding = { 'regardingobjectid_quote@odata.bind': `/quotes(${quoteId})` }
    regardingDescription = `Quote (${quoteId})`
    regardingUrl += `&etn=quote&id=${quoteId}`
  }

  // After potential contact-email update above, contact's stored email == singleNewEmail (if any).
  // Bind contact party for any recipient whose email matches the contact's effective email.
  const effectiveContactEmail = singleNewEmail
    ? singleNewEmail.trim().toLowerCase()
    : normalizedContactEmail
  const buildRecipientParty = (email: string, mask: number): EmailActivityParty => {
    const matchesContact = !!effectiveContactEmail && email.trim().toLowerCase() === effectiveContactEmail
    return matchesContact
      ? { 'partyid_contact@odata.bind': `/contacts(${contactId})`, participationtypemask: mask }
      : { addressused: email, participationtypemask: mask }
  }

  const activityParties: EmailActivityParty[] = [
    {
      'partyid_systemuser@odata.bind': `/systemusers(${fromUserId})`,
      participationtypemask: 1, // From
    },
    ...toEmails.map((email) => buildRecipientParty(email, 2)),
    ...ccEmails.map((email) => buildRecipientParty(email, 3)),
  ]

  // Wrap the body with consistent font styling
  const preparedInlineImage = prepareInlineEmailImage(body, inlineImage)
  const styledBody = wrapEmailBodyWithFontStyle(preparedInlineImage.body)

  // Create the email record
  const emailPayload = {
    subject,
    description: styledBody,
    ...regardingBinding,
    email_activity_parties: activityParties,
  }

  const emailId = await createRecord('emails', emailPayload, '', true)

  if (preparedInlineImage.inlineImage) {
    await createRecord(
      'activitymimeattachments',
      {
        'objectid_email@odata.bind': `/emails(${emailId})`,
        objecttypecode: 'email',
        subject: 'Branch client communications logo',
        body: preparedInlineImage.inlineImage.base64,
        filename: preparedInlineImage.inlineImage.fileName,
        mimetype: preparedInlineImage.inlineImage.mimeType,
        attachmentcontentid: preparedInlineImage.inlineImage.contentId,
      },
      '',
      true
    )
  }

  // Send the email using the SendEmail action
  promises.push(
    dynamicsApi.post(`/emails(${emailId})/Microsoft.Dynamics.CRM.SendEmail`, {
      IssueSend: true,
    })
  )

  // Create annotation on the quote to track that an email was sent
  // This is done when the regarding is NOT the quote itself
  if ((quoteType === QuoteType.ServiceInvoice && workOrderId) || opportunityId) {
    const emailUrl =
      env.VITE_DYNAMICS_BASE_URL +
      `/main.aspx?appid=33c294e0-f49c-ef11-8a6b-000d3a56b8ad&pagetype=entityrecord&etn=email&id=${emailId}`
    const dateWithTimezone = new Date().toLocaleString(undefined, {
      timeZoneName: 'short',
    })
    const recipientLine = `to ${toEmails.join(', ')}${ccEmails.length > 0 ? ` (CC: ${ccEmails.join(', ')})` : ''}`
    const annotationBody = {
      'objectid_quote@odata.bind': `/quotes(${quoteId})`,
      subject: 'Email Sent via CPQ',
      notetext: `<p>An email was sent ${recipientLine} on ${dateWithTimezone}.</p>
<p></p>
<p>Email: <a href="${emailUrl}" target="_blank">${subject}</a></p>
<p></p>
<p>Regarding: <a href="${regardingUrl}" target="_blank">${regardingDescription}</a></p>`,
    }

    promises.push(createRecord('annotations', annotationBody, '', true))
  }

  const results = await Promise.allSettled(promises)

  // Email send is always the first promise — must succeed
  const sendResult = results[0]
  if (sendResult?.status === 'rejected') {
    throw sendResult.reason instanceof Error ? sendResult.reason : new Error(String(sendResult.reason ?? 'Failed to send email.'))
  }

  // Annotation failure is non-blocking — log but don't throw
  const annotationResult = results[1]
  if (annotationResult?.status === 'rejected') {
    trackException(annotationResult.reason, {
      context: 'emails.sendProposalEmail',
      detail: 'Annotation creation failed after email was sent',
    })
  }

  trackEvent('email_sent')
  return { success: true, emailId }
}
